import { Platform } from 'react-native'
import { colors } from './theme'
import * as Notifications from 'expo-notifications'
import * as Device from 'expo-device'
import { supabase } from './supabase'
import { storage } from './storage'

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
})

const PUSH_TOKEN_CACHE_KEY = 'push_token_local'

async function getCachedToken() {
  try {
    const cached = await storage.get(PUSH_TOKEN_CACHE_KEY)
    return cached ?? null
  } catch { return null }
}

async function setCachedToken(token) {
  try { await storage.set(PUSH_TOKEN_CACHE_KEY, token) } catch {}
}

async function clearCachedToken() {
  try { await storage.remove(PUSH_TOKEN_CACHE_KEY) } catch {}
}

async function upsertPushToken(userId, token) {
  const { error } = await supabase
    .from('dim_user_push_tokens')
    .upsert(
      {
        id_user: userId,
        expo_push_token: token,
        platform: Platform.OS,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id_user,expo_push_token' }
    )
  if (error) throw error
}

export async function registerForPushNotifications(userId) {
  if (!Device.isDevice) return null
  if (Platform.OS === 'web') return null

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Virtua Cycling',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: colors.rosa,  // ← colors.rosa statico, ok per notifiche Android
      sound: 'default',
      enableLights: true,
      enableVibrate: true,
      showBadge: true,
    })
  }

  const { status: existingStatus } = await Notifications.getPermissionsAsync()
  let finalStatus = existingStatus

  if (existingStatus !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync()
    finalStatus = status
  }

  if (finalStatus !== 'granted') return null

  const MAX_ATTEMPTS = 3
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const tokenData = await Notifications.getExpoPushTokenAsync({
        projectId: process.env.EXPO_PUBLIC_PROJECT_ID,
      })
      const token = tokenData.data

      if (userId && token) {
        await upsertPushToken(userId, token)
        await supabase
          .from('dim_user')
          .update({ expo_push_token: token, device_platform: Platform.OS })
          .eq('id_user', userId)
        await setCachedToken(token)
      }

      return token
    } catch (e) {
      console.warn(`[Notifications] Tentativo ${attempt}/${MAX_ATTEMPTS} fallito:`, e.message)
      if (attempt < MAX_ATTEMPTS) {
        await new Promise(res => setTimeout(res, attempt * 2000))
      } else {
        console.error('[Notifications] Tutti i tentativi falliti — token non salvato')
      }
    }
  }

  return null
}

export async function refreshPushTokenIfChanged(userId) {
  if (!Device.isDevice) return
  if (Platform.OS === 'web') return

  try {
    const tokenData = await Notifications.getExpoPushTokenAsync({
      projectId: process.env.EXPO_PUBLIC_PROJECT_ID,
    })
    const currentToken = tokenData.data
    const cachedToken = await getCachedToken()

    if (cachedToken === currentToken) return

    await upsertPushToken(userId, currentToken)
    await supabase
      .from('dim_user')
      .update({ expo_push_token: currentToken, device_platform: Platform.OS })
      .eq('id_user', userId)
    await setCachedToken(currentToken)
  } catch (e) {
    console.warn('[Notifications] refreshPushTokenIfChanged:', e.message)
  }
}

export async function unregisterPushNotifications(userId) {
  if (!userId || Platform.OS === 'web') return
  try {
    const tokenData = await Notifications.getExpoPushTokenAsync({
      projectId: process.env.EXPO_PUBLIC_PROJECT_ID,
    }).catch(() => null)

    const currentToken = tokenData?.data

    if (currentToken) {
      await supabase
        .from('dim_user_push_tokens')
        .delete()
        .eq('id_user', userId)
        .eq('expo_push_token', currentToken)
    }

    await supabase
      .from('dim_user')
      .update({ expo_push_token: null })
      .eq('id_user', userId)

    await clearCachedToken()
  } catch (e) {
    console.error('[Notifications] Error unregistering:', e)
  }
}